/* TowardsGM Pro paywall + checkout, backed by Creem (functions/creem.js,
 * functions/index.js#createCreemCheckout / #creemWebhook — see
 * docs/PAYMENTS.md for the full wiring and what Pablo still has to supply).
 *
 * Clicking a plan opens Creem's own hosted checkout in this tab; Creem
 * redirects back here with `?pro=success` on success, and it is the
 * `creemWebhook` Cloud Function — not this component — that actually flips
 * `entitlements/{uid}.pro`. App.jsx notices that query param and passes
 * `checkoutReturn`, which puts this modal into a "confirming" state that
 * re-reads the entitlement a few times rather than trusting the first read,
 * since the webhook can lag the redirect by a second or two.
 *
 * STILL NO PRICES HERE, deliberately, same reasoning as the painted-door
 * version this replaced: Creem's own checkout page is the one place that
 * quotes a number, so there is never a claim on this screen that isn't
 * backed by a working purchase path right behind it. The locks and perk list
 * stay exactly as they were.
 *
 * While CHECKOUT_PAUSED (creemCheckout.js), picking a plan records it in
 * proInterest/{uid} instead and the modal says so — no Creem call at all.
 *
 * In the Android/iOS shells (!WEB_CHECKOUT, same file) there are no plan
 * buttons at all: one "tell me when Pro is available in the app" button records
 * the same interest doc. There is no price and no way to pay.
 *
 * What the shell DOES say, in plain text, is that Pro is sold on the website
 * and unlocks here when the same account signs in. Play's Payments FAQ allows
 * exactly that for a consumption-only app ("information about purchasing
 * options without direct links") and nothing more — so it is a sentence,
 * never a link, a button or a price. `pro-web-hint` below; pinned by
 * e2e/native-store-policy.spec.js, including the absence of a link.
 *
 * Since 2026-10-22 the shell also helps the user get there, still without a
 * link or a price: "Email me the link" mails the website address to the
 * account's own verified address and "Copy link" puts it on the clipboard
 * (proHandoff.js). Both are buttons that act on text; neither navigates. The
 * rule and its risk are in TODO.md › Rules that bind. Every step is tagged:
 * pro_handoff_{email_tapped,email_sent,email_failed,copy_tapped,copied} here,
 * pro_handoff_landed on the website, and proInterest/{uid} records the same
 * taps consent-free (handoffEmailAt / handoffCopyAt) for functions/metrics.js.
 *
 * MEASUREMENT. Google Analytics is opt-in, so it undercounts by exactly the
 * people who declined. The consent-free funnel is proInterest/{uid}, written on
 * every plan tap (web) and every "tell me" tap (shells) with `platform`, and
 * joined against entitlements in functions/metrics.js — tapped Pro, never
 * subscribed is the difference. The GA events below add the where and why. */
import { useEffect, useMemo, useRef, useState } from "react";
import { C, ELEVATION, GHOST, PRIMARY_COMPACT, SECONDARY_COMPACT, T, TONES, surface } from "./theme.js";
import { Label } from "./Ui.jsx";
import { proPitch } from "./learn/proPitch.js";
import { trackEvent } from "./firebase.js";
import { subscribeAuth } from "./auth.js";
import { AuthPanel } from "./AuthPanel.jsx";
import { authError } from "./authStyles.js";
import { loadLearn } from "./learn/persist.js";
import { WEB_HINT_KEY, isProUser, refreshEntitlements, subscribePro } from "./entitlements.js";
import { CHECKOUT_PAUSED, WEB_CHECKOUT, startCheckout } from "./creemCheckout.js";
import { saveProInterest } from "./proInterest.js";
import { copyHandoffLink, emailBlockedReason, sendHandoffEmail } from "./proHandoff.js";
import { onHardwareBack, platform } from "./native.js";
import { useT } from "./i18n.js";

/* [planId, label key, hint key-or-null]. Two plans to match the taxonomy
 * `proInterest` docs already use — adding a third is just another row here
 * plus a matching CREEM_PRODUCT_ID_* param in functions/index.js. */
const PLANS = [
  ["monthly", "pro.planMonthly", null],
  ["annual", "pro.planAnnual", "pro.planAnnualHint"],
];

/* What a store build offers in their place: no plan (there is nothing to
 * choose between where nothing can be bought), just the wish to hear about it.
 * "notify" is the button's id here and in analytics, never a plan — the
 * interest doc records `plan: null` for it, see choosePlan. */
const NOTIFY_ONLY = [["notify", "pro.notifyInApp", null]];

/* Either reason ends in the same place: record the interest, charge nothing. */
const INTEREST_ONLY = CHECKOUT_PAUSED || !WEB_CHECKOUT;

/* Pro sells DEPTH — the deep tail, of the catalog and of your own mined deck —
 * and never the feedback loop. The daily due session, the rating trend and the
 * recommended next course moved to free (product_improvements_v1.md D1), so
 * nothing here may promise them back. "First in line for … deeper personal
 * insights" was dropped for exactly that reason: those insights are free now,
 * and a perk list that implies otherwise is the same misleading-claim problem
 * the prices were removed for.
 *
 * The third line used to read "Every mistake mined from your games, including
 * the ones no course covers", and it was false: the Prep Report's own "Drill
 * them" button has always handed the free tier the entire mined deck, uncovered
 * positions included. What Pro actually adds there is DEPTH — the deck is
 * ranked by damage and cut, and Pro raises the cut (D2/D4). Free keeps its
 * twenty; nothing moved out of it. */
const PERKS = [
  ["♛", "pro.perkMasterLines"],
  ["♜", "pro.perkNewFirst"],
  ["♝", "pro.perkWholeDeck"],
  ["♞", "pro.perkAllDevices"],
];

/* Every paywall event says which surface it came from. The Android app runs
 * the same JS SDK in a WebView, so GA's own platform field reads "web" for it. */
const track = (name, params = {}) => trackEvent(name, { platform, ...params });

export default function ProModal({ source, courseId, belowCap = 0, checkoutReturn = false, annualFirst = false, onClose }) {
  const translate = useT()
  const [{ user }, setAuthState] = useState({ user: null, ready: false });
  useEffect(() => subscribeAuth(setAuthState), []);
  const needsAccount = !user || user.isAnonymous;
  const [proUser, setProUser] = useState(isProUser());
  useEffect(() => subscribePro(setProUser), []);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [interestSaved, setInterestSaved] = useState(false);
  /* Shell-only: the send-me-the-link state. `email` is idle | sending | sent |
   * error; `blocked` is why the email button is withheld (null = offered). */
  const [email, setEmail] = useState("idle");
  const [copied, setCopied] = useState(null);
  const [blocked, setBlocked] = useState("checking");
  useEffect(() => {
    if (WEB_CHECKOUT || needsAccount) return;
    let live = true;
    emailBlockedReason(user).then((reason) => {
      if (!live) return;
      setBlocked(reason);
      track("pro_handoff_shown", { source, email_offered: !reason, ...(reason ? { blocked: reason } : {}) });
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAccount, user?.uid]);
  const [confirming, setConfirming] = useState(checkoutReturn && !isProUser());

  /* D3 · the user's own numbers, read straight off the stored coaching record
   * rather than passed in. That record is persisted and catalog-free, so this
   * works from the nav's ♛ button in App.jsx exactly as it does from inside a
   * course — and there is no second code path that could disagree. Null for
   * anyone with no graded leaks; the perk list is the fallback, not a
   * placeholder. */
  const pitch = useMemo(() => proPitch({ record: loadLearn().coachingRecord, belowCap, source }), [belowCap, source]);

  useEffect(() => {
    track("pro_paywall_shown", { source, ...(courseId ? { course_id: courseId } : {}) });
    if (!WEB_CHECKOUT) {
      try {
        localStorage.setItem(WEB_HINT_KEY, "1");
      } catch {
        /* a private window: the hint still shows, only the follow-up is lost */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* The purchase the whole checkout exists for, seen from the return trip:
   * Creem sent the browser back with ?pro=success AND the webhook has flipped
   * the entitlement. `pro_subscribe_clicked` without this, a day later, is an
   * abandoned checkout. Once per mount — the modal re-renders as it confirms. */
  const confirmedRef = useRef(false);
  useEffect(() => {
    if (checkoutReturn && proUser && !confirmedRef.current) {
      confirmedRef.current = true;
      track("pro_purchase_confirmed", { source });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutReturn, proUser]);
  useEffect(
    () =>
      onHardwareBack(() => {
        onClose();
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* Up to 4 re-checks, ~1.5s apart, then give up spinning — the webhook
   * fires within a second or two of a real checkout in practice, and a
   * confirmation screen that spins forever on a genuinely stuck webhook is
   * worse than one that hands back an honest "still working on it". */
  useEffect(() => {
    if (!confirming || !user?.uid) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts++;
      await refreshEntitlements(user.uid);
      if (cancelled) return;
      if (isProUser() || attempts >= 4) {
        setConfirming(false);
        return;
      }
      setTimeout(tick, 1500);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirming, user?.uid]);

  const choosePlan = async (planId) => {
    setError(null);
    setPending(planId);
    track("pro_subscribe_clicked", { plan: planId, source, ...(courseId ? { course_id: courseId } : {}) });
    if (!INTEREST_ONLY) {
      /* The consent-free half of the funnel, written BEFORE the redirect and
       * not awaited: checkout must never wait on a bookkeeping write, and a
       * failed one only costs a data point. */
      saveProInterest({ uid: user?.uid, email: user?.email, plan: planId, source, courseId });
    }
    if (INTEREST_ONLY) {
      const saved = await saveProInterest({ uid: user?.uid, email: user?.email, plan: WEB_CHECKOUT ? planId : null, source, courseId });
      setPending(null);
      if (saved) {
        setInterestSaved(true);
        track("pro_interest_recorded", { plan: planId, source });
      } else {
        setError(true);
      }
      return;
    }
    try {
      await startCheckout(planId);
      // Success navigates the browser to Creem's checkout — nothing left to
      // do here, and this component is about to unmount.
    } catch (err) {
      setPending(null);
      // Only ever used as a boolean gate below — translate("pro.checkoutError")
      // is what's actually shown, so no reason to hold the raw message text.
      setError(true);
      track("pro_checkout_error", { plan: planId, source, code: err?.code || null });
    }
  };

  const emailLink = async () => {
    setEmail("sending");
    track("pro_handoff_email_tapped", { source });
    const result = await sendHandoffEmail(user, { source });
    if (result.ok) {
      setEmail("sent");
      track("pro_handoff_email_sent", { source, repeat: result.reason === "already_sent" });
      saveProInterest({ uid: user.uid, email: user.email, plan: null, source, courseId, extra: { handoffEmailAt: Date.now() } });
    } else {
      setEmail("error");
      track("pro_handoff_email_failed", { source, reason: result.reason });
    }
  };
  const copyLink = async () => {
    track("pro_handoff_copy_tapped", { source });
    const ok = await copyHandoffLink({ source });
    setCopied(ok);
    track("pro_handoff_copied", { source, ok });
    if (ok) saveProInterest({ uid: user.uid, email: user.email, plan: null, source, courseId, extra: { handoffCopyAt: Date.now() } });
  };

  const dismiss = () => {
    if (!pending) track("pro_paywall_dismissed", { source });
    onClose();
  };

  return (
    <div
      onClick={dismiss}
      style={{ position: "fixed", inset: 0, zIndex: 55, background: "rgba(10,15,12,0.74)", display: "grid", placeItems: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pro-modal"
        style={{
          ...surface(),
          ...T.body,
          border: `1px solid ${TONES.accent.edge}`,
          boxShadow: ELEVATION.overlay,
          color: C.ink,
          maxHeight: "86vh",
          overflowY: "auto",
          padding: 22,
          width: "min(100%, 430px)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <Label tone="accent">{translate("pro.kicker")}</Label>
          <button type="button" onClick={dismiss} aria-label={translate("pro.closeAria")} style={{ ...GHOST, color: C.sub, marginRight: -4 }}>
            {translate("pro.close")}
          </button>
        </div>

        {checkoutReturn && proUser ? (
          <div data-testid="pro-welcome" style={{ padding: "8px 0 4px" }}>
            <div style={{ ...T.display, color: C.ink, margin: "4px 0 10px" }}>{translate("pro.welcomeToPro")}</div>
            <p style={{ ...T.small, color: C.sub, margin: "0 0 16px" }}>{translate("pro.welcomeBody")}</p>
            <button
              type="button"
              onClick={onClose}
              style={{ ...PRIMARY_COMPACT, width: "100%" }}
            >
              {translate("pro.done")}
            </button>
          </div>
        ) : checkoutReturn && confirming ? (
          <div data-testid="pro-confirming" style={{ padding: "8px 0 4px" }}>
            <div style={{ ...T.display, color: C.ink, margin: "4px 0 10px" }}>{translate("pro.confirmingPurchase")}</div>
            <p style={{ ...T.small, color: C.sub, margin: "0 0 16px" }}>{translate("pro.confirmingBody")}</p>
          </div>
        ) : checkoutReturn ? (
          <div data-testid="pro-still-confirming" style={{ padding: "8px 0 4px" }}>
            <div style={{ ...T.display, color: C.ink, margin: "4px 0 10px" }}>{translate("pro.stillConfirming")}</div>
            <p style={{ ...T.small, color: C.sub, margin: "0 0 16px" }}>{translate("pro.stillConfirmingBody")}</p>
            <button
              type="button"
              onClick={onClose}
              style={{ ...PRIMARY_COMPACT, width: "100%" }}
            >
              {translate("pro.done")}
            </button>
          </div>
        ) : interestSaved ? (
          <div data-testid="pro-interest-recorded" style={{ padding: "8px 0 4px" }}>
            <div style={{ ...T.display, color: C.ink, margin: "4px 0 10px" }}>{translate("pro.interestTitle")}</div>
            <p style={{ ...T.small, color: C.sub, margin: "0 0 8px" }}>
              {translate(WEB_CHECKOUT ? "pro.interestBody" : "pro.interestBodyInApp")}
            </p>
            <p style={{ ...T.caption, color: C.sub, margin: "0 0 16px" }}>{translate("pro.interestNoCharge")}</p>
            <button
              type="button"
              onClick={onClose}
              style={{ ...PRIMARY_COMPACT, width: "100%" }}
            >
              {translate("pro.done")}
            </button>
          </div>
        ) : needsAccount ? (
          <div data-testid="pro-needs-account">
            <div style={{ ...T.display, color: C.ink, margin: "2px 0 10px" }}>{translate("pro.createAccountFirst")}</div>
            <p style={{ ...T.small, color: C.sub, margin: "0 0 16px" }}>
              {translate("pro.followsAccount")}
            </p>
            <AuthPanel onComplete={() => track("pro_account_created", { source })} surface="pro_modal" />
          </div>
        ) : (
          <>
            <div style={{ ...T.display, color: C.ink, margin: "2px 0 12px" }}>{translate("pro.unlock")}</div>
            {pitch ? (
              <div
                data-testid="pro-pitch"
                style={{ ...surface("accent"), marginBottom: 16, padding: "11px 13px" }}
              >
                <div style={{ ...T.body, color: C.ink, fontWeight: 700 }}>{pitch.lead}</div>
                <div style={{ ...T.caption, color: C.sub, marginTop: 4 }}>{pitch.detail}</div>
              </div>
            ) : null}
            <div style={{ display: "grid", gap: 9, marginBottom: 18 }}>
              {PERKS.map(([glyph, key]) => (
                <div key={key} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                  <span aria-hidden="true" style={{ color: C.brass, fontFamily: "Georgia,serif", fontSize: 16, flexShrink: 0 }}>{glyph}</span>
                  <span style={{ ...T.small, color: C.ink }}>{translate(key)}</span>
                </div>
              ))}
            </div>
            {WEB_CHECKOUT ? null : (
              <p data-testid="pro-web-hint" style={{ ...T.small, color: C.ink, margin: "0 0 14px" }}>
                {translate("pro.webHint")}
              </p>
            )}
            {WEB_CHECKOUT ? null : (
              <div data-testid="pro-handoff" style={{ display: "grid", gap: 9, marginBottom: 12 }}>
                {blocked || blocked === "checking" ? null : email === "sent" ? (
                  <div data-testid="pro-handoff-sent" style={{ ...surface("accent"), padding: "11px 13px" }}>
                    <div style={{ ...T.body, color: C.ink, fontWeight: 700 }}>{translate("pro.handoffSentTitle")}</div>
                    <div style={{ ...T.caption, color: C.sub, marginTop: 4 }}>{translate("pro.handoffSentBody", { email: user.email })}</div>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="pro-handoff-email"
                    onClick={emailLink}
                    disabled={email === "sending"}
                    style={{ ...PRIMARY_COMPACT, cursor: email === "sending" ? "default" : "pointer", padding: "12px 16px", width: "100%" }}
                  >
                    {email === "sending" ? translate("pro.handoffSending") : translate("pro.handoffEmail")}
                    {email !== "sending" ? (
                      <span style={{ ...T.caption, display: "block", fontWeight: 400, marginTop: 2, opacity: 0.85 }}>
                        {translate("pro.handoffEmailHint", { email: user.email })}
                      </span>
                    ) : null}
                  </button>
                )}
                {email === "error" ? (
                  <div data-testid="pro-handoff-error" style={authError}>{translate("pro.handoffError")}</div>
                ) : null}
                <button
                  type="button"
                  data-testid="pro-handoff-copy"
                  onClick={copyLink}
                  style={{ ...(blocked || blocked === "checking" ? PRIMARY_COMPACT : SECONDARY_COMPACT), padding: "11px 16px", width: "100%" }}
                >
                  {copied ? translate("pro.handoffCopied") : translate("pro.handoffCopy")}
                </button>
                {copied === false ? <div data-testid="pro-handoff-copy-error" style={authError}>{translate("pro.handoffCopyError")}</div> : null}
              </div>
            )}
            <div style={{ display: "grid", gap: 9 }}>
              {(WEB_CHECKOUT ? (annualFirst ? [...PLANS].reverse() : PLANS) : NOTIFY_ONLY).map(([planId, labelKey, hintKey], i) => (
                <button
                  key={planId}
                  data-testid={WEB_CHECKOUT ? `pro-checkout-${planId}` : "pro-notify"}
                  onClick={() => choosePlan(planId)}
                  disabled={Boolean(pending)}
                  style={{
                    ...(!WEB_CHECKOUT ? { ...GHOST, color: C.sub } : annualFirst && i > 0 ? SECONDARY_COMPACT : PRIMARY_COMPACT),
                    cursor: pending ? "default" : "pointer",
                    opacity: pending && pending !== planId ? 0.5 : 1,
                    padding: "12px 16px",
                    width: "100%",
                  }}
                >
                  {pending === planId
                    ? translate(INTEREST_ONLY ? "pro.savingInterest" : "pro.startingCheckout")
                    : translate(labelKey)}
                  {hintKey && pending !== planId ? (
                    <span style={{ ...T.caption, display: "block", fontWeight: 400, marginTop: 2, opacity: 0.85 }}>{translate(hintKey)}</span>
                  ) : null}
                </button>
              ))}
            </div>
            {error ? (
              <div data-testid="pro-checkout-error" style={{ ...authError, marginTop: 10 }}>
                {translate(INTEREST_ONLY ? "pro.interestError" : "pro.checkoutError")}
              </div>
            ) : null}
            <button
              type="button"
              onClick={dismiss}
              style={{ ...GHOST, color: C.sub, display: "flex", margin: "12px auto 0" }}
            >
              {translate("pro.maybeLater")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
